import {
  Box,
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  Typography
} from "@mui/material"
import {
  AiPromptUsage,
  FilterFormValues,
  GeminiModels,
  GPTModels,
  IAiPrompts
} from "@pages/main/scripts/prompts"
import {
  layersOfPrompt,
  promptLayerLabel,
  promptRoleLabel,
  promptRowKey
} from "@pages/main/scripts/prompts/prompt.models"
import { layerChipStyles } from "@pages/main/scripts/prompts/sub_components/PromptLayerBar"
import { useMemo } from "react"
import type { UseFormWatch } from "react-hook-form"

type Row =
  | { kind: "header"; usage: string; count: number }
  | { kind: "item"; prompt: IAiPrompts }

type PromptListProps = {
  /** The rows of the layer being browsed. */
  prompts: IAiPrompts[]
  /** Every row, whatever its layer — needed to mark which names a newer engine has forked. */
  allPrompts: IAiPrompts[]
  /** Only in the ALL view, where a name appears once per layer and the chip is what tells them apart. */
  showLayer: boolean
  watchFilter: UseFormWatch<FilterFormValues>
  sortMode: "NAME" | "VERSION_DATE"
  selectedPrompt: IAiPrompts | null
  setSelectedPrompt: (p: IAiPrompts | null) => void
  bulkMode: boolean
  bulkSelectedKeys: ReadonlySet<string>
  onBulkToggle: (keys: string[], checked: boolean) => void
  onBulkEdit: () => void
}

export const PromptList = ({
  prompts,
  allPrompts,
  showLayer,
  watchFilter,
  sortMode,
  selectedPrompt,
  setSelectedPrompt,
  bulkMode,
  bulkSelectedKeys,
  onBulkToggle,
  onBulkEdit
}: PromptListProps) => {
  const search = watchFilter("search")
  const filterRole = watchFilter("role")
  const filterUsage = watchFilter("usage")
  const filterAiModelType = watchFilter("aiModelType")
  const hasResponseSchema = watchFilter("hasResponseSchema")

  const filtered = useMemo(() => {
    return prompts.filter((p) => {
      // Check AI Model Type filter
      if (filterAiModelType) {
        const isGPT = Object.values(GPTModels).includes(p.aiModel as GPTModels)
        const isGemini = Object.values(GeminiModels).includes(
          p.aiModel as GeminiModels
        )

        // If filter is a group (GPT or GEMINI)
        if (filterAiModelType === "GPT" && !isGPT) return false
        if (filterAiModelType === "GEMINI" && !isGemini) return false

        // If filter is a specific model
        if (
          filterAiModelType !== "GPT" &&
          filterAiModelType !== "GEMINI" &&
          String(p.aiModel) !== filterAiModelType
        ) {
          return false
        }
      }

      // "SHARED" is the row every role falls back to, which is stored as a null role.
      const matchesRole = filterRole
        ? (p.role ?? "SHARED") === filterRole
        : true

      return (
        matchesRole &&
        (filterUsage ? (p.usage ?? "UNDEFINED") === filterUsage : true) &&
        (search ? p.name.toLowerCase().includes(search.toLowerCase()) : true) &&
        (hasResponseSchema ? p.responseSchema != null : true)
      )
    })
  }, [
    prompts,
    filterRole,
    filterUsage,
    filterAiModelType,
    search,
    hasResponseSchema
  ])

  function latestUpdatedAt(p: IAiPrompts): number {
    const src = p.updatedAt ?? p.createdAt
    const t = src ? new Date(src).getTime() : 0
    return t
  }

  function formatDateSmall(d: Date): string {
    return d.toLocaleDateString("tr-TR", {
      year: "2-digit",
      month: "2-digit",
      day: "2-digit"
    })
  }

  const rows: Row[] = useMemo(() => {
    if (sortMode === "NAME") {
      // kategorili + alfabetik
      const usages = [...Object.values(AiPromptUsage), "UNDEFINED"]
      const out: Row[] = []
      usages.forEach((usage) => {
        const items = filtered
          .filter(
            (p) => (p.usage ?? "UNDEFINED").toString() === usage.toString()
          )
          .sort((a, b) => a.name.localeCompare(b.name))

        if (items.length === 0) return
        out.push({ kind: "header", usage, count: items.length })
        items.forEach((p) => out.push({ kind: "item", prompt: p }))
      })
      return out
    }

    // tek liste, updatedAt desc
    return filtered
      .slice()
      .sort((a, b) => latestUpdatedAt(b) - latestUpdatedAt(a))
      .map((p) => ({ kind: "item", prompt: p }))
  }, [filtered, sortMode])

  const visibleKeys = useMemo(() => filtered.map(promptRowKey), [filtered])
  const visibleSelected = visibleKeys.filter((k) =>
    bulkSelectedKeys.has(k)
  ).length
  const hiddenSelected = bulkSelectedKeys.size - visibleSelected

  function groupKeys(usage: string) {
    return filtered
      .filter((p) => (p.usage ?? "UNDEFINED").toString() === usage)
      .map(promptRowKey)
  }

  const renderRow = (row: Row) => {
    if (row.kind === "header") {
      const keys = bulkMode ? groupKeys(row.usage) : []
      const selectedInGroup = keys.filter((k) => bulkSelectedKeys.has(k)).length
      return (
        <Typography
          key={`h-${row.usage}`}
          variant="subtitle2"
          sx={{
            px: 1.5,
            py: 1,
            opacity: 0.7,
            display: "flex",
            justifyContent: "space-between",
            fontWeight: "bold"
          }}
        >
          <span>
            {bulkMode && (
              <Checkbox
                size="small"
                sx={{ p: 0, mr: 1 }}
                checked={selectedInGroup > 0 && selectedInGroup === keys.length}
                indeterminate={
                  selectedInGroup > 0 && selectedInGroup < keys.length
                }
                onChange={(e) => onBulkToggle(keys, e.target.checked)}
              />
            )}
            {row.usage === "UNDEFINED" ? "Undefined Category" : row.usage}
          </span>
          <span>({row.count})</span>
        </Typography>
      )
    }

    const p = row.prompt
    const getModelStyles = (model: string) => {
      const m = model.toLowerCase()

      let hash = 0
      for (let i = 0; i < m.length; i++) {
        hash = (hash << 5) - hash + m.charCodeAt(i)
        hash |= 0
      }
      const hVal = Math.abs(hash)

      let hue = 200
      if (m.includes("gpt-6")) hue = 15
      else if (m.includes("gpt-4o")) hue = 158
      else if (m.includes("gpt-4.1")) hue = 212
      else if (m.includes("gpt-5.2")) hue = 265
      else if (m.includes("gpt-5.1")) hue = 250
      else if (
        m.includes("gpt-5.0") ||
        (m.includes("gpt-5") && !m.includes("5."))
      )
        hue = 235
      else if (m.includes("gemini-3")) hue = 325
      else if (m.includes("gemini-2.5")) hue = 275
      else if (m.includes("gemini")) hue = 290
      else if (m.includes("gpt-3")) hue = 190
      else hue = 35

      const isSmall =
        m.includes("mini") ||
        m.includes("nano") ||
        m.includes("lite") ||
        m.includes("luna")
      const isPremium =
        m.includes("pro") || m.includes("ultra") || m.includes("preview")

      const hueJitter = (hVal % 31) - 15
      const satJitter = (hVal % 11) - 5
      const lightJitter = ((hVal % 7) - 3) / 2

      let finalHue = hue + hueJitter
      let sat = 75 + satJitter
      let bgLight = 94 + lightJitter
      let textLight = 35 + lightJitter

      if (isSmall) {
        finalHue += 25
        sat = 45 + satJitter
        bgLight = 97 + lightJitter
        textLight = 50 + lightJitter
      } else if (isPremium) {
        finalHue -= 20
        sat = 85 + satJitter
        bgLight = 92 + lightJitter
        textLight = 25 + lightJitter
      }

      return {
        bg: `hsl(${finalHue}, ${sat}%, ${bgLight}%)`,
        color: `hsl(${finalHue}, ${sat}%, ${textLight}%)`,
        border: `hsl(${finalHue}, ${sat}%, ${bgLight - 10}%)`
      }
    }

    const modelStyles = getModelStyles(p.aiModel)
    const key = promptRowKey(p)
    const bulkChecked = bulkSelectedKeys.has(key)

    return (
      <ListItem key={key} disablePadding>
        <ListItemButton
          selected={
            bulkMode
              ? bulkChecked
              : !!selectedPrompt && promptRowKey(selectedPrompt) === key
          }
          onClick={() =>
            bulkMode ? onBulkToggle([key], !bulkChecked) : setSelectedPrompt(p)
          }
        >
          {bulkMode && (
            <Checkbox
              size="small"
              edge="start"
              tabIndex={-1}
              disableRipple
              checked={bulkChecked}
              sx={{ p: 0, mr: 1 }}
            />
          )}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%"
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
                gap: 0.2
              }}
            >
              <Typography variant="body2" noWrap sx={{ fontSize: "0.8rem" }}>
                {p.name}
              </Typography>
              <Box sx={{ display: "flex", gap: 0.4, alignItems: "center" }}>
                {showLayer && (
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      ...layerChipStyles(p.engineVersion ?? null),
                      borderRadius: "4px",
                      px: 0.5,
                      width: "fit-content",
                      fontSize: "0.6rem",
                      fontWeight: "bold",
                      lineHeight: 1.2,
                      textTransform: "uppercase"
                    }}
                  >
                    {promptLayerLabel(p.engineVersion)}
                  </Box>
                )}
                {/* On BASE, the versions that have their own copy and will NOT see an edit here. */}
                {!p.engineVersion &&
                  layersOfPrompt(allPrompts, p.name).map((layer) => (
                    <Box
                      key={layer}
                      title={`${layer} has its own copy of ${p.name}`}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        ...layerChipStyles(layer),
                        borderRadius: "4px",
                        px: 0.5,
                        width: "fit-content",
                        fontSize: "0.6rem",
                        fontWeight: "bold",
                        lineHeight: 1.2
                      }}
                    >
                      ↗ {layer}
                    </Box>
                  ))}
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    bgcolor: p.role ? "#EDE7F6" : "#ECEFF1",
                    color: p.role ? "#4527A0" : "#455A64",
                    border: `1px solid ${p.role ? "#B39DDB" : "#CFD8DC"}`,
                    borderRadius: "4px",
                    px: 0.5,
                    width: "fit-content",
                    fontSize: "0.6rem",
                    fontWeight: "bold",
                    lineHeight: 1.2,
                    textTransform: "uppercase"
                  }}
                >
                  {promptRoleLabel(p.role)}
                </Box>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    bgcolor: modelStyles.bg,
                    color: modelStyles.color,
                    border: `1px solid ${modelStyles.border}`,
                    borderRadius: "4px",
                    px: 0.5,
                    width: "fit-content",
                    fontSize: "0.6rem",
                    fontWeight: "bold",
                    lineHeight: 1.2,
                    textTransform: "uppercase"
                  }}
                >
                  {p.aiModel}
                </Box>
              </Box>
            </Box>

            <Typography variant="caption" sx={{ opacity: 0.6 }}>
              {formatDateSmall(new Date(p.updatedAt ?? p.createdAt))}
            </Typography>
          </Box>
        </ListItemButton>
      </ListItem>
    )
  }
  return (
    <List dense disablePadding>
      {bulkMode && (
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1,
            bgcolor: "background.paper",
            borderBottom: "1px solid",
            borderColor: "divider",
            px: 1.5,
            py: 0.5,
            display: "flex",
            alignItems: "center",
            gap: 1
          }}
        >
          <Checkbox
            size="small"
            sx={{ p: 0 }}
            checked={
              visibleKeys.length > 0 && visibleSelected === visibleKeys.length
            }
            indeterminate={
              visibleSelected > 0 && visibleSelected < visibleKeys.length
            }
            onChange={(e) => onBulkToggle(visibleKeys, e.target.checked)}
          />
          <Typography variant="caption" sx={{ flex: 1 }}>
            {bulkSelectedKeys.size} selected
            {hiddenSelected > 0 && ` (${hiddenSelected} hidden by filter)`}
          </Typography>
          <Button
            size="small"
            disabled={bulkSelectedKeys.size === 0}
            onClick={() => onBulkToggle(Array.from(bulkSelectedKeys), false)}
          >
            Clear
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={bulkSelectedKeys.size === 0}
            onClick={onBulkEdit}
          >
            Edit
          </Button>
        </Box>
      )}
      {rows.map((row) => renderRow(row))}
    </List>
  )
}
